import styled from 'styled-components';
import { shade } from 'polished';
import theme from '../../theme';

const darkBlue = shade(0.4, theme.cores.login);

export const PageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #eef1f8 0%, #e6ebf8 45%, #f1eef8 100%);

  @media (max-width: 760px) {
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
    padding: 24px 0;
  }
`;

export const PageAura = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
`;

export const AuraBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  will-change: transform;
`;

export const IntroOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(120% 120% at 50% 40%, #101f36 0%, #05070a 78%);
  will-change: opacity, transform;
`;

export const IntroLogo = styled.img`
  position: relative;
  z-index: 1;
  width: 180px;
  height: auto;
`;

export const Card = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  width: min(900px, 92vw);
  min-height: 560px;
  border-radius: 28px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow:
    0 44px 90px -24px rgba(18, 28, 55, 0.4),
    0 18px 44px -18px rgba(47, 111, 237, 0.3);
  opacity: 0;
  will-change: opacity, transform;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    z-index: 2;
    background: linear-gradient(90deg, #2f6fed, #8f5ff0, #2f6fed);
  }

  @media (max-width: 760px) {
    flex-direction: column;
    width: 92vw;
    min-height: 0;
  }
`;

export const LeftPanel = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 40px;
  background: linear-gradient(160deg, #0b1b2e 0%, ${darkBlue} 130%);

  @media (max-width: 760px) {
    min-height: 260px;
    padding: 32px 24px;
  }
`;

export const DecorLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
`;

export const PanelGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 480px;
  height: 480px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(79, 134, 255, 0.32) 0%,
    rgba(111, 77, 224, 0.14) 45%,
    rgba(111, 77, 224, 0) 72%
  );
  opacity: 0;
  will-change: transform, opacity;
`;

export const OrbitLayer = styled.div`
  position: absolute;
  inset: 0;
  will-change: transform;

  @media (max-width: 760px) {
    display: none;
  }
`;

export const MobileIconRow = styled.div`
  display: none;
  gap: 10px;
  margin-top: 18px;
  opacity: 0;

  @media (max-width: 760px) {
    display: flex;
  }
`;

export const MobileIconChip = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.82);
`;

export const IconOrb = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.09);
  opacity: 0;
  will-change: transform, opacity;
`;

export const IconOrbInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BrandBlock = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 320px;

  @media (max-width: 760px) {
    text-align: center;
  }
`;

export const BadgePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  margin-bottom: 18px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  opacity: 0;
`;

export const FormLogo = styled.img`
  width: 190px;
  height: auto;
  opacity: 0;
`;

export const BrandName = styled.span`
  margin-top: 14px;
  color: ${theme.cores.white};
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.5px;
  opacity: 0;
`;

export const Greeting = styled.span`
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.65);
  font-size: 13px;
  opacity: 0;
`;

export const Tagline = styled.p`
  margin: 14px 0 0;
  max-width: 240px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 12.5px;
  line-height: 1.6;
  text-align: center;
  opacity: 0;
`;

export const RightPanel = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 48px 40px;
`;

export const DecorLayerLight = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
`;

export const LightBlob = styled.div`
  position: absolute;
  border-radius: 50%;
  will-change: transform;
`;

export const PastelBubble = styled.div`
  position: absolute;
  border-radius: 42% 58% 65% 35% / 45% 45% 55% 55%;
  background: #cfe0ff;
  opacity: 0;
  will-change: transform;
`;

export const FormContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 310px;

  h1 {
    font-size: 23px;
    color: ${theme.cores.black};
    text-align: center;
    margin: 0 0 6px;
    opacity: 0;
  }
`;

export const FormSubtitle = styled.p`
  margin: 0 0 26px;
  text-align: center;
  color: #8a93a3;
  font-size: 13px;
  opacity: 0;
`;

export const Avatar = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 50%;
  border: 2px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px;
  background:
    linear-gradient(${theme.cores.white}, ${theme.cores.white}) padding-box,
    linear-gradient(135deg, ${theme.cores.login}, #8f5ff0) border-box;
  box-shadow: 0 12px 26px -10px rgba(47, 111, 237, 0.5);
  opacity: 0;

  svg {
    color: ${theme.cores.login};
  }
`;

export const InputGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 54px;
  background: ${theme.cores.lightGray2};
  border: 1.5px solid ${theme.cores.linha};
  border-radius: 18px;
  padding: 12px 18px;
  color: ${theme.cores.black};
  opacity: 0;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;

  & + div {
    margin-top: 14px;
  }

  &:focus-within {
    border-color: ${theme.cores.login};
    background: ${theme.cores.white};
    box-shadow: 0 0 0 4px rgba(47, 111, 237, 0.12);
  }
`;

export const IconChip = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(47, 111, 237, 0.1);
  color: ${theme.cores.login};
`;

export const InputWrap = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;

  input {
    width: 100%;
    background: transparent;
    border: 0;
    outline: none;
    font-size: 14px;
    color: ${theme.cores.black};

    &::placeholder {
      color: #9aa3af;
    }
  }
`;

export const LoginButton = styled.button`
  width: 100%;
  height: 54px;
  margin-top: 24px;
  border: 0;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, ${theme.cores.login} 0%, #6f4de0 100%);
  color: ${theme.cores.white};
  font-weight: bold;
  font-size: 15px;
  cursor: pointer;
  opacity: 0;
  will-change: transform;
  box-shadow: 0 16px 32px -12px rgba(47, 111, 237, 0.55);

  svg {
    transition: transform 0.25s ease;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -60%;
    width: 40%;
    height: 100%;
    background: linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.35), transparent);
    transform: skewX(-20deg);
    transition: left 0.6s ease;
  }

  &:hover::before {
    left: 130%;
  }
`;

export const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  opacity: 0;

  a {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #9aa3af;
    text-decoration: none;
    font-size: 12px;
    transition: color 0.2s;

    &:hover {
      color: ${theme.cores.login};
    }
  }

  .instagram-link {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    justify-content: center;
    border: 1px solid ${theme.cores.linha};
  }
`;
